import React, { FC } from 'react'
import cx from 'classnames'
import { PriceManager } from 'prices/priceManager'
import styles from 'sections/PodAtc/styles/PriceBlock.module.scss'
import { trackAffirmLearnMoreClick } from 'events/index'
import { AffirmLocations } from 'constants/events'
import { RegionShort } from '../../utils/internationalization'
import { Currency } from 'prices/types'
import AffirmLineBF from 'sections/PodAtc/AffirmLineBF'
import { TruemedBlueLogo } from 'components/_const_assets/truemedLogo'
import { TruemedInfo } from 'components/TruemedInfo/TruemedInfo'

export interface PriceBlockProps {
	defaultSavingsText: string
	amount: number
	oldAmount: number | null
	hideAffirm: boolean
	affirmLocation: AffirmLocations
	currency: Currency
	region: RegionShort
	addMembership?: boolean
	monthlyPrice: string
	greyLearnMore?: boolean
	provider?: string
}

const financingProviderLogo = (
	// eslint-disable-next-line @next/next/no-img-element
	<img
		src="https://eightsleep.imgix.net/affirm_logo_large_a815f2f5-d910-4233-8ee6-3f37cebf5315.png?v=1598625209"
		alt="Affirm logo"
	/>
)

const FinancingComboWithTruemed: FC<PriceBlockProps> = (props) => {
	const affirmBlock = props.hideAffirm ? (
		<></>
	) : (
		<>
			<div className={styles.money_separator}>
				<span>OR</span>
			</div>
			<div className={styles.money_wrapper}>
				<div className={styles.money_box}>
					<p className={styles.moneytitle}>PAY OVER TIME</p>
					<div className={styles.money_banner_content}>
						<div className={styles.left_banner}>{financingProviderLogo}</div>
						<div>
							<AffirmLineBF
								currentPrice={props.amount}
								newFinancing={true}
								id={'cart-affirm-trigger-container'}
								monthlyPrice={props.monthlyPrice}
							/>
						</div>
						<a
							data-provider="Affirm"
							data-track="['click','mouseover'], 'Financing', 'Pre qualify button for Affirm'"
							className={cx(styles.trigger_p, { [styles.trigger_grey]: props.greyLearnMore }, 'affirm-site-modal')}
							role="button"
							aria-label="Pre qualify button for affirm"
						>
							Learn more
						</a>
						<p
							data-page-type="cart"
							data-amount={props.amount * 100}
							onClick={() => trackAffirmLearnMoreClick(props.affirmLocation)}
							className={cx('affirm-as-low-as', 'none', 'affirm-trigger-container')}
						/>
					</div>
				</div>
			</div>
		</>
	)
	return (
		<>
			<div className={cx(styles.money_wrapper)}>
				<div className={styles.money_box}>
					<p className={styles.moneytitle}>PAY NOW</p>
					<div>
						<span
							className={styles.pricestyle}
							id="cart-pay-now-price"
						>
							{PriceManager.formatPriceToCurrencyNoDecimal(props.amount, 'USD')}{' '}
						</span>
						{props.amount > 0 && !!props.oldAmount && props.amount !== props.oldAmount && (
							<span className={styles.originalprice}>{PriceManager.formatPriceToCurrencyNoDecimal(props.oldAmount, 'USD')}</span>
						)}
					</div>
				</div>
			</div>
			{affirmBlock}
			<div className={styles.money_separator}>
				<span>OR</span>
			</div>
			<div className={styles.money_wrapper}>
				<div className={styles.money_box}>
					<div className={styles.money_banner_content}>
						<div>
							<TruemedBlueLogo />
						</div>
						<div>
							<p className={styles.moneytitle}>FSA/HSA Eligible</p>
						</div>
						<div>
							<TruemedInfo />
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export const PriceBlock: FC<PriceBlockProps> = (props) => {
	const showTrumed = props.currency === 'USD'

	let _text = 'Buy now'
	if (props.oldAmount && props.oldAmount > props.amount) {
		const diff = PriceManager.formatPriceToCurrencyNoDecimal(props.oldAmount - props.amount, props.currency)
		_text = props.defaultSavingsText ? `${props.defaultSavingsText} ${diff} off` : `You're saving ${diff}`
	}

	if (props.region === 'ca') {
		return null
	}

	if (props.region === '' && props.amount < 50) {
		return null
	}

	const renderMainComponent = showTrumed ? (
		<FinancingComboWithTruemed {...props} />
	) : (
		<>
			<div className={cx(styles.moneyline, styles.banner_financing)}>
				<div className={styles.moneyleft}>
					<p className={styles.moneytitle}>PAY NOW</p>
					<div className={styles.pay_over_time_container}>
						<span>
							<span
								className={styles.pricestyle}
								id="cart-pay-now-price"
							>
								{PriceManager.formatPriceToCurrencyNoDecimal(props.amount, 'USD')}{' '}
							</span>
							{props.amount > 0 && !!props.oldAmount && props.amount !== props.oldAmount && (
								<span className={styles.originalprice}>{PriceManager.formatPriceToCurrencyNoDecimal(props.oldAmount, 'USD')}</span>
							)}
						</span>
					</div>
					{props.addMembership ? <p className={styles.price_subtext}>plus 8+ Pro membership</p> : null}
				</div>
				<div className={styles.moneydivider}>
					<div className={styles.moneyborderline} />
				</div>
				<div className={styles.moneyright}>
					<p className={styles.moneytitle}>PAY OVER TIME</p>
					<div className={styles.pay_over_time_container}>
						<AffirmLineBF
							currentPrice={props.amount}
							newFinancing={true}
							id={'cart-affirm-trigger-container'}
							monthlyPrice={props.monthlyPrice}
						/>
					</div>
					{props.addMembership ? <p className={styles.price_subtext}>plus 8+ Pro membership</p> : null}
				</div>
			</div>
			{!props?.hideAffirm && (
				<div className={cx(styles.financing_options, styles.financing_options_cart)}>
					<div className={styles.payment_option}>
						{financingProviderLogo}
						<p className={styles.finance_text}>Financing starting from 0% APR with $0 down.</p>
						<a
							data-provider="Affirm"
							data-track="['click','mouseover'], 'Financing', 'Pre qualify button for Affirm'"
							className={cx(styles.trigger_p, { [styles.trigger_grey]: props.greyLearnMore }, 'affirm-site-modal')}
							role="button"
							aria-label="Pre qualify button for affirm"
						>
							Learn more
						</a>
					</div>
				</div>
			)}
			<p
				data-page-type="cart"
				data-amount={props.amount * 100}
				onClick={() => trackAffirmLearnMoreClick(props.affirmLocation)}
				className={cx('affirm-as-low-as', 'none', 'affirm-trigger-container')}
			/>
		</>
	)

	return (
		<div className={cx(styles.priceblock)}>
			<div className={cx(styles.banner, styles.banner_financing)}>
				<p className={cx(styles.bannertext)}>{_text}</p>
			</div>
			{renderMainComponent}
		</div>
	)
}
